/**
 * ELECTROSTATIC ENERGISER EXTENSION PACK.
 *
 * These are amplifiers that drive electrostatic headphones directly: they
 * supply a fixed polarising bias (580 V for the STAX "Pro" standard) and swing
 * several hundred volts of audio onto the stators. They are not headphone
 * amplifiers in the ordinary sense and cannot be substituted for one.
 *
 * WHY THIS IS A SEPARATE FILE, NOT PART OF THE CORE 100
 * The core amplifier database holds exactly one model per manufacturer, which
 * is the constraint the project was specified against. Woo Audio (GES), HeadAmp
 * (Blue Hawaii SE) and Mjolnir Audio (KGSSHV Carbon) already occupy their slot
 * there. This pack sits alongside the core database, is registered into the
 * same registry and is indistinguishable to the DSP -- it simply is not counted
 * against the one-model-per-brand rule. This is exactly the extensibility the
 * registry was built for: adding these cost one file and zero changes anywhere
 * else.
 *
 * Two requested models are NOT here because they are already in the core
 * database and are well authored there: HeadAmp Blue Hawaii SE
 * (amp.headamp.blue-hawaii-se) and Mjolnir Audio KGSSHV Carbon
 * (amp.mjolnir-audio.kgsshv-carbon).
 *
 * PROVENANCE WARNING -- READ BEFORE TRUSTING ANY NUMBER HERE
 * Electrostatic energisers are a boutique and substantially DIY-derived corner
 * of audio. Several of these are hand-built in small numbers by individual
 * builders from published circuit designs (many of them Kevin Gilmore's), and
 * they have no datasheet at all. Where that is the case the profile says so
 * explicitly and carries ESTIMATED or UNKNOWN confidence. Three entries in this
 * pack could NOT be verified against any source and are marked UNKNOWN: treat
 * them as placeholders with a plausible topology, not as descriptions of a
 * specific measured device.
 *
 * Output voltage is the parameter that matters most here, because the engine
 * computes drive headroom from it against the headphone's sensitivity. A
 * SR-L700MK2 at 101 dB / 100 Vrms needs roughly 280 Vrms for a 110 dB peak, so
 * an energiser quoted below about 300 Vrms will audibly run out of swing.
 */

import type { AmpProfile } from '../../types/hardware';
import { HARMONIC_SHAPES, makeAmp, usd } from '../kit';

export const AMPS_ELECTROSTATIC: AmpProfile[] = [
  makeAmp({
    mfr: 'HeadAmp',
    model: 'Aristaeus',
    price: usd(24995, 'APPROXIMATE', 2024, 'Approximate; limited production and pricing has not been consistently published.'),
    year: 2023,
    country: 'United States',
    topology: 'tube-set',
    form: 'electrostatic-energiser',
    devices: '300B directly-heated triode output, single-ended, direct stator drive',
    power: { biasVolts: 580, outputVrms: 420, confidence: 'ESTIMATED' },
    gainDb: 56,
    thdPercent: 0.5,
    snrDb: 92,
    bandwidth: [8, 45000],
    outputImpedanceOhms: 1800,
    feedbackDb: 0,
    outputTransformer: false,
    electrostaticDrive: true,
    estatBias: 'pro-580v',
    crosstalkDb: -68,
    harmonicShape: HARMONIC_SHAPES.set,
    nonlinear: { kind: 'triode', amount: 0.8, asymmetry: 0.75, bias: 0.8, headroomDb: 10, oversample: 8 },
    dynamics: { sagDb: 1.5, sagTimeMs: 220, recoveryMs: 520, slewRateVus: 20, headroomDb: 1.4 },
    clipping: { kind: 'soft', kneeDb: 9, stickiness: 0.35 },
    confidence: 'ESTIMATED',
    accent: '#e8a33d',
    tags: ['extension-pack', 'electrostatic', 'energiser', 'stax-pro-bias', '300b', 'set', 'zero-feedback', 'flagship'],
    notes:
      'A directly-heated 300B triode running single-ended straight onto the stators with no global feedback. This is the opposite design philosophy to the Carbon: distortion is roughly an order of magnitude higher, strongly second-harmonic biased, and rises about 1 dB per dB of drive rather than staying at a fixed floor. Against an electrostatic element -- which contributes almost no distortion of its own -- the amplifier becomes the dominant nonlinearity in the chain, which is unusual and clearly audible in A/B.',
    provenanceNote:
      'Output device and single-ended zero-feedback topology from HeadAmp public description. Output voltage, THD, SNR, bandwidth and price are ESTIMATED; no specification sheet has been published for this model.',
  }),

  makeAmp({
    mfr: 'Mjolnir Audio',
    model: 'KGST',
    price: usd(2800, 'APPROXIMATE', 2024, 'Built to order; price varies with chassis and options.'),
    year: 2013,
    country: 'Iceland',
    topology: 'tube-set',
    form: 'electrostatic-energiser',
    devices: 'Kevin Gilmore Singled-Ended Triode: 6S4A output tubes with a solid-state current-source front end',
    power: { biasVolts: 580, outputVrms: 300, confidence: 'ESTIMATED' },
    gainDb: 54,
    thdPercent: 0.22,
    snrDb: 96,
    bandwidth: [6, 55000],
    outputImpedanceOhms: 1600,
    feedbackDb: 4,
    outputTransformer: false,
    electrostaticDrive: true,
    estatBias: 'pro-580v',
    crosstalkDb: -70,
    harmonicShape: HARMONIC_SHAPES.set,
    nonlinear: { kind: 'triode', amount: 0.6, asymmetry: 0.6, bias: 0.62, headroomDb: 12, oversample: 8 },
    dynamics: { sagDb: 1.0, sagTimeMs: 180, recoveryMs: 420, slewRateVus: 26, headroomDb: 1.6 },
    clipping: { kind: 'soft', kneeDb: 8, stickiness: 0.3 },
    confidence: 'ESTIMATED',
    accent: '#c0a17a',
    tags: ['extension-pack', 'electrostatic', 'energiser', 'stax-pro-bias', 'hybrid', '6s4a', 'kevin-gilmore', 'diy-derived'],
    notes:
      'Single-ended 6S4A output with a solid-state front end. Sits deliberately between the Carbon and the all-tube designs: a clear second-harmonic signature from the single-ended output stage, but far less of it than a 300B design, and with roughly 300 Vrms available it is the entry point at which a Lambda-series element starts to be driven properly rather than comfortably.',
    provenanceNote:
      'Topology from the published Kevin Gilmore KGST design and Mjolnir Audio descriptions. All electrical figures are ESTIMATED; there is no datasheet for this amplifier.',
  }),

  makeAmp({
    mfr: 'Woo Audio',
    model: 'WES',
    price: usd(4990, 'ORIGINAL_RETAIL', 2016, 'Discontinued; original US retail.'),
    year: 2010,
    country: 'United States',
    topology: 'tube-otl',
    form: 'electrostatic-energiser',
    devices: '4 x 6SN7 driver, 4 x 6S4A output, transformerless direct drive',
    power: { biasVolts: 580, outputVrms: 380, confidence: 'ESTIMATED' },
    gainDb: 55,
    thdPercent: 0.25,
    snrDb: 94,
    bandwidth: [6, 48000],
    outputImpedanceOhms: 1900,
    feedbackDb: 5,
    outputTransformer: false,
    electrostaticDrive: true,
    estatBias: 'pro-580v',
    crosstalkDb: -69,
    harmonicShape: HARMONIC_SHAPES.otl,
    nonlinear: { kind: 'triode', amount: 0.58, asymmetry: 0.42, bias: 0.5, headroomDb: 12, oversample: 8 },
    dynamics: { sagDb: 1.1, sagTimeMs: 200, recoveryMs: 460, slewRateVus: 24, headroomDb: 1.7 },
    clipping: { kind: 'soft', kneeDb: 8, stickiness: 0.3 },
    confidence: 'ESTIMATED',
    accent: '#d4a05a',
    tags: ['extension-pack', 'electrostatic', 'energiser', 'stax-pro-bias', 'otl', '6s4a'],
    notes:
      'The larger sibling of the GES: twice the output tube complement, correspondingly more available swing and lower supply sag on sustained peaks. Same OTL character -- H3-dominant with a substantial H2 component -- but it holds composure on dynamic material where the GES is already sagging.',
    provenanceNote:
      'Tube complement and bias standard from Woo Audio published material. Output voltage, THD, SNR and bandwidth are ESTIMATED; Woo Audio does not publish full specifications for its electrostatic amplifiers.',
  }),

  makeAmp({
    mfr: 'Woo Audio',
    model: '3ES',
    price: usd(5900, 'ORIGINAL_RETAIL', 2016, 'Discontinued; original US retail.'),
    year: 2012,
    country: 'United States',
    topology: 'tube-otl',
    form: 'electrostatic-energiser',
    devices: '2 x 5687 driver, 4 x 6S4A output, choke-loaded transformerless direct drive',
    power: { biasVolts: 580, outputVrms: 400, confidence: 'ESTIMATED' },
    gainDb: 55,
    thdPercent: 0.18,
    snrDb: 96,
    bandwidth: [5, 52000],
    outputImpedanceOhms: 1750,
    feedbackDb: 6,
    outputTransformer: false,
    electrostaticDrive: true,
    estatBias: 'pro-580v',
    crosstalkDb: -71,
    harmonicShape: HARMONIC_SHAPES.otl,
    nonlinear: { kind: 'triode', amount: 0.52, asymmetry: 0.38, bias: 0.45, headroomDb: 13, oversample: 8 },
    dynamics: { sagDb: 0.85, sagTimeMs: 190, recoveryMs: 430, slewRateVus: 27, headroomDb: 1.9 },
    clipping: { kind: 'soft', kneeDb: 7.5, stickiness: 0.28 },
    confidence: 'ESTIMATED',
    accent: '#c98c4e',
    tags: ['extension-pack', 'electrostatic', 'energiser', 'stax-pro-bias', 'otl', '5687'],
    notes:
      'Uses a 5687 driver rather than the 6SN7 of the GES and WES, which raises the driver stage transconductance and lowers driver-stage distortion. Modelled with slightly lower THD, less sag and marginally more swing than the WES, so the three Woo energisers here form a genuine progression rather than three copies of one model.',
    provenanceNote:
      'Tube complement and bias standard from Woo Audio published material. All electrical figures are ESTIMATED; Woo Audio does not publish full specifications for its electrostatic amplifiers.',
  }),

  makeAmp({
    mfr: 'STAX',
    model: 'SRM-006tS',
    price: usd(1350, 'CURRENT_RETAIL', 2023),
    year: 2012,
    country: 'Japan',
    topology: 'hybrid',
    form: 'electrostatic-energiser',
    devices: 'Solid-state input and voltage amplification, 6FQ7/6CG7 tube output stage',
    power: { biasVolts: 580, outputVrms: 300, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 54,
    thdPercent: 0.01,
    snrDb: 105,
    bandwidth: [3, 90000],
    outputImpedanceOhms: 1200,
    feedbackDb: 14,
    outputTransformer: false,
    electrostaticDrive: true,
    estatBias: 'pro-580v',
    crosstalkDb: -80,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { kind: 'triode', amount: 0.3, asymmetry: 0.3, bias: 0.3, headroomDb: 15, oversample: 8 },
    dynamics: { sagDb: 0.45, sagTimeMs: 150, recoveryMs: 320, slewRateVus: 35, headroomDb: 1.5 },
    clipping: { kind: 'soft', kneeDb: 6, stickiness: 0.2 },
    confidence: 'MANUFACTURER_SPEC',
    accent: '#a8d8ff',
    tags: ['extension-pack', 'electrostatic', 'energiser', 'stax-pro-bias', 'hybrid', 'oem-pairing'],
    notes:
      'The manufacturer\'s own hybrid energiser and the reference pairing for the SR-L700MK2, which makes it the natural control when evaluating the boutique amplifiers in this pack. Meaningful global feedback keeps distortion an order of magnitude below the OTL designs, but the quoted output swing is the lowest here, so it is the pairing most likely to show drive limiting on peaks -- the engine will report that headroom explicitly.',
    provenanceNote:
      'THD, frequency response, bias standard and tube complement from STAX published specifications. Output voltage and output impedance are ESTIMATED from the specification and circuit topology.',
  }),

  makeAmp({
    mfr: 'George Parris',
    model: 'T2',
    price: usd(15000, 'APPROXIMATE', 2024, 'Hand-built to order in very small numbers; no list price exists. Figure reflects reported commissions.'),
    year: 2015,
    country: 'United States',
    topology: 'hybrid',
    form: 'electrostatic-energiser',
    devices: 'Reproduction of the STAX SRM-T2 circuit: solid-state cascode front end with EL34 output, fully balanced',
    power: { biasVolts: 580, outputVrms: 600, confidence: 'UNKNOWN' },
    gainDb: 58,
    thdPercent: 0.02,
    snrDb: 106,
    bandwidth: [2, 100000],
    outputImpedanceOhms: 800,
    feedbackDb: 18,
    outputTransformer: false,
    electrostaticDrive: true,
    estatBias: 'pro-580v',
    crosstalkDb: -88,
    harmonicShape: HARMONIC_SHAPES.pushPullTube,
    nonlinear: { kind: 'pentode', amount: 0.3, asymmetry: 0.15, bias: 0.12, headroomDb: 17, oversample: 8 },
    dynamics: { sagDb: 0.35, sagTimeMs: 130, recoveryMs: 300, slewRateVus: 70, headroomDb: 3.0 },
    clipping: { kind: 'soft', kneeDb: 5.5, stickiness: 0.18 },
    confidence: 'UNKNOWN',
    accent: '#7fd4c1',
    tags: ['extension-pack', 'electrostatic', 'energiser', 'stax-pro-bias', 't2', 'balanced', 'boutique', 'unverified'],
    notes:
      'A modern reproduction of the STAX SRM-T2, an amplifier whose original production run was tiny and which has no accessible specification sheet. Modelled as the circuit family suggests: a cascoded solid-state front end driving a balanced EL34 output with meaningful feedback, giving very large swing with tube-topology harmonic structure but solid-state-order distortion magnitude.',
    provenanceNote:
      'UNVERIFIED. The T2 circuit itself is well documented, but no published specification exists for this builder\'s implementation and the manufacturer attribution could not be confirmed against a primary source. Every electrical figure here is a topology-based placeholder tagged UNKNOWN. Replace it with real data before drawing any conclusion from an A/B involving this profile.',
  }),

  makeAmp({
    mfr: 'Megatron',
    model: 'Megatron XL',
    price: usd(6000, 'APPROXIMATE', 2024, 'Hand-built; no list price. Figure is a placeholder.'),
    year: 2016,
    country: 'Unknown',
    topology: 'tube-push-pull',
    form: 'electrostatic-energiser',
    devices: 'High-voltage tube output stage, direct stator drive (specific complement unconfirmed)',
    power: { biasVolts: 580, outputVrms: 520, confidence: 'UNKNOWN' },
    gainDb: 57,
    thdPercent: 0.12,
    snrDb: 98,
    bandwidth: [4, 60000],
    outputImpedanceOhms: 1300,
    feedbackDb: 8,
    outputTransformer: false,
    electrostaticDrive: true,
    estatBias: 'pro-580v',
    crosstalkDb: -76,
    harmonicShape: HARMONIC_SHAPES.pushPullTube,
    nonlinear: { kind: 'pentode', amount: 0.5, asymmetry: 0.25, bias: 0.22, headroomDb: 14, oversample: 8 },
    dynamics: { sagDb: 0.6, sagTimeMs: 170, recoveryMs: 400, slewRateVus: 32, headroomDb: 2.3 },
    clipping: { kind: 'soft', kneeDb: 7, stickiness: 0.24 },
    confidence: 'UNKNOWN',
    accent: '#b48ead',
    tags: ['extension-pack', 'electrostatic', 'energiser', 'stax-pro-bias', 'boutique', 'unverified'],
    notes:
      'Requested addition. Modelled as a large push-pull tube energiser with generous swing and moderate feedback, which is the configuration the name is associated with in the electrostatic community.',
    provenanceNote:
      'UNVERIFIED. This model could not be confirmed against any manufacturer or primary source, and the tube complement, output voltage, distortion and bandwidth are all placeholders tagged UNKNOWN. It is included because it was explicitly requested. Do not treat any number in this profile as a description of a real device until it is replaced with measured or published data.',
  }),

  makeAmp({
    mfr: 'Grand Cayman',
    model: 'Grand Cayman',
    price: usd(4500, 'APPROXIMATE', 2024, 'Hand-built; no list price. Figure is a placeholder.'),
    year: 2018,
    country: 'Unknown',
    topology: 'hybrid',
    form: 'electrostatic-energiser',
    devices: 'Hybrid high-voltage output stage, direct stator drive (specific devices unconfirmed)',
    power: { biasVolts: 580, outputVrms: 440, confidence: 'UNKNOWN' },
    gainDb: 56,
    thdPercent: 0.05,
    snrDb: 102,
    bandwidth: [3, 80000],
    outputImpedanceOhms: 1100,
    feedbackDb: 12,
    outputTransformer: false,
    electrostaticDrive: true,
    estatBias: 'pro-580v',
    crosstalkDb: -82,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { kind: 'triode', amount: 0.34, asymmetry: 0.28, bias: 0.28, headroomDb: 15, oversample: 8 },
    dynamics: { sagDb: 0.4, sagTimeMs: 150, recoveryMs: 340, slewRateVus: 45, headroomDb: 2.4 },
    clipping: { kind: 'soft', kneeDb: 6.5, stickiness: 0.2 },
    confidence: 'UNKNOWN',
    accent: '#8fd0e8',
    tags: ['extension-pack', 'electrostatic', 'energiser', 'stax-pro-bias', 'hybrid', 'boutique', 'unverified'],
    notes:
      'Requested addition. Modelled as a hybrid energiser sitting between the all-tube and all-solid-state designs in this pack.',
    provenanceNote:
      'UNVERIFIED. This model could not be confirmed against any manufacturer or primary source. Every electrical figure is a topology-based placeholder tagged UNKNOWN. It is included because it was explicitly requested; replace it with real data before using it for comparison.',
  }),

  makeAmp({
    mfr: 'Konka',
    model: 'Novalis',
    price: usd(7000, 'APPROXIMATE', 2024, 'Hand-built; no list price. Figure is a placeholder.'),
    year: 2019,
    country: 'Unknown',
    topology: 'tube-set',
    form: 'electrostatic-energiser',
    devices: 'Single-ended tube output stage, direct stator drive (specific complement unconfirmed)',
    power: { biasVolts: 580, outputVrms: 360, confidence: 'UNKNOWN' },
    gainDb: 55,
    thdPercent: 0.35,
    snrDb: 93,
    bandwidth: [7, 46000],
    outputImpedanceOhms: 1700,
    feedbackDb: 2,
    outputTransformer: false,
    electrostaticDrive: true,
    estatBias: 'pro-580v',
    crosstalkDb: -67,
    harmonicShape: HARMONIC_SHAPES.set,
    nonlinear: { kind: 'triode', amount: 0.7, asymmetry: 0.68, bias: 0.7, headroomDb: 11, oversample: 8 },
    dynamics: { sagDb: 1.3, sagTimeMs: 210, recoveryMs: 480, slewRateVus: 22, headroomDb: 1.5 },
    clipping: { kind: 'soft', kneeDb: 8.5, stickiness: 0.32 },
    confidence: 'UNKNOWN',
    accent: '#ff9f6e',
    tags: ['extension-pack', 'electrostatic', 'energiser', 'stax-pro-bias', 'set', 'boutique', 'unverified'],
    notes:
      'Requested addition. Modelled as a low-feedback single-ended tube energiser, which gives it the most pronounced second-harmonic signature in this pack after the Aristaeus.',
    provenanceNote:
      'UNVERIFIED. Neither the manufacturer nor the model could be confirmed against any primary source. Every electrical figure is a topology-based placeholder tagged UNKNOWN. It is included because it was explicitly requested; replace it with real data before drawing conclusions from it.',
  }),
];

/** Entries whose existence or specifications could not be verified. */
export const UNVERIFIED_ELECTROSTATIC_IDS = AMPS_ELECTROSTATIC.filter(
  (a) => a.confidence === 'UNKNOWN',
).map((a) => a.id);
